import React, { useState, useContext, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Formik } from 'formik';
import * as Yup from 'yup';

const Register = () => {
  const { register, error, isLoading, currentUser } = useContext(AuthContext);
  const [registerError, setRegisterError] = useState('');
  const navigate = useNavigate();
  const isManager = currentUser && currentUser.role === 'manager';

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Initial form values
  const initialValues = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'developer', // Default role
    hourly_rate: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setRegisterError('');
    try {
      console.log('Starting registration process...');
      
      // Verify token exists (debug)
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found in localStorage');
        setRegisterError('You must be logged in as an admin to register new users');
        setSubmitting(false);
        return;
      }
      
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...userData } = values;
      
      console.log('Submitting user data:', userData);
      const response = await register(userData);
      
      console.log('Registration response:', response);
      
      if (response && response.user) {
        // Show success message and reset form
        alert(`User ${values.name} has been successfully registered as a ${values.role}`);
        resetForm();
        // Navigate back to admin dashboard
        navigate('/admin/dashboard');
      } else {
        setRegisterError('Registration failed: Unexpected response format');
      }
    } catch (err) {
      console.error('Registration error in component:', err);
      let errorMessage = 'Registration failed';
      
      // Extract more detailed error message if available
      if (err.response && err.response.data) {
        errorMessage = err.response.data.msg || err.response.data.error || 'Registration failed';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setRegisterError(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-fade-in">
      <Container>
        <Row className="justify-content-center mt-4">
          <Col md={6} lg={5}>
            <div className="text-center mb-4">
              <div style={{
                width: 52, height: 52, borderRadius: '14px', margin: '0 auto 12px',
                background: 'linear-gradient(135deg, #7C6FA0, #6BA3C7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(124,111,160,0.25)'
              }}>
                <span style={{ fontSize: '1.4rem', color: '#fff' }}>&#128100;</span>
              </div>
              <h3 style={{ fontWeight: 700, color: '#2D2D2D', marginBottom: 4 }}>Register New User</h3>
              <p style={{ color: '#9E9E9E', fontSize: '0.88rem' }}>
                {isManager ? 'Create accounts for team members' : 'Create accounts for any role'}
              </p>
            </div>

            <Card style={{ border: '1px solid #EEEEEE', borderRadius: 16, boxShadow: '0 4px 20px rgba(124,111,160,0.08)' }}>
              <Card.Body style={{ padding: '1.75rem' }}>
                {(registerError || error) && (
                  <Alert variant="danger" style={{ borderRadius: 10, fontSize: '0.88rem' }}>{registerError || error}</Alert>
                )}
                
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.name && errors.name}
                          placeholder="Enter full name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.email && errors.email}
                          placeholder="Enter email address"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              type="password"
                              name="password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.password && errors.password}
                              placeholder="Min 6 characters"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.password}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                              type="password"
                              name="confirmPassword"
                              value={values.confirmPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.confirmPassword && errors.confirmPassword}
                              placeholder="Re-enter password"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmPassword}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                              name="role"
                              value={values.role}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="developer">Developer</option>
                              <option value="designer">Designer</option>
                              <option value="marketing">Marketing</option>
                              <option value="manager">Manager</option>
                              {!isManager && <option value="admin">Admin</option>}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Hourly Rate (₹)</Form.Label>
                            <Form.Control
                              type="number"
                              name="hourly_rate"
                              value={values.hourly_rate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.hourly_rate && errors.hourly_rate}
                              placeholder="e.g. 500"
                              min="0"
                              step="0.01"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.hourly_rate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="d-grid gap-2 mt-2">
                        <Button 
                          variant="primary" 
                          type="submit" 
                          disabled={isSubmitting || isLoading}
                          style={{ padding: '0.65rem', fontWeight: 600, borderRadius: 10 }}
                        >
                          {isLoading ? 'Creating User...' : 'Create User'}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Register;
